import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PhoneCall, CheckCircle, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { safeSetItem } from "@/lib/safe-storage";
import { format } from "date-fns";
import StreamlinedBookingFlow from "./StreamlinedBookingFlow";
import { getUserTimezone } from "@/lib/timezone-utils";
import { trackLead } from "@/lib/meta-pixel";

interface DiscoveryCallBookingProps {
  mentorId: string;
  mentorName: string;
  mentorSpecialty: string;
  autoOpen?: boolean;
}

export const DiscoveryCallBooking: React.FC<DiscoveryCallBookingProps> = ({
  mentorId,
  mentorName,
  mentorSpecialty,
  autoOpen = false
}) => {
  const [hasUsedDiscoveryCall, setHasUsedDiscoveryCall] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showBookingFlow, setShowBookingFlow] = useState(autoOpen);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    checkDiscoveryCallUsage();
  }, [mentorId]);

  useEffect(() => {
    // Handle autoOpen prop changes (for post-auth redirect)
    if (autoOpen && !isLoading) {
      // Open regardless; component UI will still show "already used" notice if applicable
      setShowBookingFlow(true);
    }
  }, [autoOpen, isLoading]);

  // Ensure we pick up auth becoming available right after redirect
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const authed = !!session?.user;
      setIsAuthenticated(authed);
      if (authed && session?.user) {
        setIsLoading(true);
        try {
          await checkDiscoveryCallUsage(session.user.id);
        } catch (e) {
          console.error('Auth listener recheck failed', e);
        } finally {
          setIsLoading(false);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [mentorId]);

  const checkDiscoveryCallUsage = async (providedUserId?: string) => {
    try {
      const userId = providedUserId || (await supabase.auth.getUser()).data.user?.id;
      if (!userId) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      setIsAuthenticated(true);

      // Check if there is ANY discovery call (past or future) between this student and mentor
      // Discovery calls are ONE-TIME ONLY per student-mentor pair
      const { data: discoveryCalls, error } = await supabase
        .from('sessions')
        .select('id, scheduled_at, status')
        .eq('student_id', userId)
        .eq('mentor_id', mentorId)
        .eq('is_discovery_call', true)
        .limit(1);

      if (error) throw error;

      setHasUsedDiscoveryCall(!!(discoveryCalls && discoveryCalls.length > 0));
    } catch (error) {
      console.error('Error checking discovery call usage:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookDiscoveryCall = async () => {
    if (!selectedSlot) {
      toast({
        title: "Select a Time",
        description: "Please select a time slot for your discovery call.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to book a discovery call.",
          variant: "destructive",
        });
        return;
      }

      // Create the session as a discovery call (30 minutes, 0 credits)
      // The selectedSlot.date is a Date object, and start_time/end_time are in HH:mm format
      // We need to interpret these in the USER'S local timezone, then convert to UTC
      const dateStr = format(selectedSlot.date, 'yyyy-MM-dd');
      // Prefer UTC datetime computed from the slot (based on mentor timezone), fallback to user-local reconstruction
      let localDateTime: Date;
      if (selectedSlot.utcDateTime) {
        localDateTime = new Date(selectedSlot.utcDateTime);
      } else {
        const [hours, minutes] = selectedSlot.start_time.split(':').map(Number);
        const tmp = new Date(selectedSlot.date);
        tmp.setHours(hours, minutes, 0, 0);
        localDateTime = tmp;
      }
      const utcIso = (selectedSlot.utcDateTime ? new Date(selectedSlot.utcDateTime) : localDateTime).toISOString();
      
      // Prevent booking past times
      if (new Date(utcIso).getTime() <= Date.now()) {
        toast({
          title: "Time Unavailable",
          description: "Please pick a future time slot.",
          variant: "destructive",
        });
        return;
      }

      // Prevent booking discovery call if ANY discovery call exists (past or future)
      const { data: existing, error: existErr } = await supabase
        .from('sessions')
        .select('id, status')
        .eq('student_id', user.id)
        .eq('mentor_id', mentorId)
        .eq('is_discovery_call', true)
        .limit(1);

      if (existErr) throw existErr;
      if (existing && existing.length > 0) {
        toast({
          title: "Discovery Call Already Used",
          description: "You've already had a discovery call with this mentor. Please book a regular session.",
          variant: "destructive",
        });
        return;
      }

      // CRITICAL: Validate booking against mentor availability on server-side, with local fallback
      const localValidateBooking = async (): Promise<{ valid: boolean; reason?: string }> => {
        try {
          // Mentor timezone
          const { data: mentor, error: mentorErr } = await supabase
            .from('mentors')
            .select('timezone')
            .eq('id', mentorId)
            .single();
          const mentorTz = mentor?.timezone || 'America/Chicago';

          // Determine day of week in mentor TZ
          let dayOfWeek: number = 0;
          if (selectedSlot.utcDateTime) {
            const weekdayFmt = new Intl.DateTimeFormat('en-US', { timeZone: mentorTz, weekday: 'short' });
            const weekdayStr = weekdayFmt.format(new Date(selectedSlot.utcDateTime));
            const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
            dayOfWeek = map[weekdayStr as keyof typeof map];
          } else {
            dayOfWeek = new Date(dateStr + 'T00:00:00Z').getUTCDay();
          }

          // Availability
          const { data: availabilitySlots, error: availError } = await supabase
            .from('mentor_availability')
            .select('*')
            .eq('mentor_id', mentorId)
            .eq('day_of_week', dayOfWeek)
            .eq('is_active', true);

          if (availError) return { valid: false, reason: 'Unable to verify availability' };
          if (!availabilitySlots || availabilitySlots.length === 0) {
            return { valid: false, reason: "Mentor has no availability on this day" };
          }

          const isWithinAvailability = availabilitySlots.some((slot: any) => {
            const toMins = (t: string) => {
              const parts = t.split(':');
              const h = parseInt(parts[0], 10) || 0;
              const m = parseInt(parts[1], 10) || 0;
              return h * 60 + m;
            };
            const selStart = toMins(selectedSlot.start_time);
            const selEnd = toMins(selectedSlot.end_time);
            const slotStart = toMins(slot.start_time);
            const slotEnd = toMins(slot.end_time);
            return selStart >= slotStart && selEnd <= slotEnd;
          });
          if (!isWithinAvailability) {
            return { valid: false, reason: "Requested time is outside of mentor's availability hours" };
          }

          // Blackout date in mentor TZ
          let mentorLocalDate = dateStr;
          try {
            const ymdFmt = new Intl.DateTimeFormat('en-CA', { timeZone: mentorTz, year: 'numeric', month: '2-digit', day: '2-digit' });
            const parts = ymdFmt.formatToParts(selectedSlot.utcDateTime ? new Date(selectedSlot.utcDateTime) : selectedSlot.date);
            const y = parts.find(p => p.type === 'year')?.value;
            const m = parts.find(p => p.type === 'month')?.value;
            const d = parts.find(p => p.type === 'day')?.value;
            mentorLocalDate = `${y}-${m}-${d}`;
          } catch {}

          const { data: blackoutDates } = await supabase
            .from('mentor_blackout_dates')
            .select('id')
            .eq('mentor_id', mentorId)
            .eq('blackout_date', mentorLocalDate);

          if (blackoutDates && blackoutDates.length > 0) {
            return { valid: false, reason: "Mentor is unavailable on this date (blackout date)" };
          }

          // Conflict by UTC datetime
          let conflict = false;
          if (selectedSlot.utcDateTime) {
            const { data: existingByUtc } = await supabase
              .from('sessions')
              .select('id')
              .eq('mentor_id', mentorId)
              .eq('scheduled_at', new Date(selectedSlot.utcDateTime).toISOString())
              .in('status', ['pending', 'scheduled', 'in_progress']);
            conflict = !!(existingByUtc && existingByUtc.length > 0);
          }
          if (!conflict) {
            const { data: existingSessions } = await supabase
              .from('sessions')
              .select('id')
              .eq('mentor_id', mentorId)
              .eq('scheduled_date', dateStr)
              .eq('scheduled_start_time', selectedSlot.start_time)
              .in('status', ['pending', 'scheduled', 'in_progress']);
            conflict = !!(existingSessions && existingSessions.length > 0);
          }
          if (conflict) return { valid: false, reason: "This time slot is already booked" };

          return { valid: true };
        } catch (e) {
          console.error('Local validation failed', e);
          return { valid: false, reason: 'Validation failed' };
        }
      };

      let edgeValidated = false;
      try {
        const { data: validationResult, error: validationError } = await supabase.functions.invoke('validate-session-booking', {
          body: {
            mentorId,
            scheduledDate: dateStr,
            scheduledStartTime: selectedSlot.start_time,
            scheduledEndTime: selectedSlot.end_time,
            utcDateTime: selectedSlot.utcDateTime ? new Date(selectedSlot.utcDateTime).toISOString() : undefined
          }
        });
        if (validationError) {
          console.warn('Edge validation error; using local fallback', validationError);
        } else if (!validationResult?.valid) {
          toast({
            title: "Time Unavailable",
            description: validationResult?.reason || 'This time slot is not available',
            variant: "destructive",
          });
          return;
        } else {
          edgeValidated = true;
        }
      } catch (err) {
        console.warn('Edge validation exception; using local fallback', err);
      }

      if (!edgeValidated) {
        const local = await localValidateBooking();
        if (!local.valid) {
          toast({
            title: "Time Unavailable",
            description: local.reason || 'This time slot is not available',
            variant: "destructive",
          });
          return;
        }
      }

      // Create the session via secure edge function to avoid RLS issues
      const { data: bookData, error: bookError } = await supabase.functions.invoke('book-discovery-call', {
        body: {
          mentorId: mentorId,
          studentId: user.id,
          scheduledDate: dateStr,
          scheduledStartTime: selectedSlot.start_time,
          scheduledEndTime: selectedSlot.end_time,
          utcDateTime: (selectedSlot.utcDateTime ? new Date(selectedSlot.utcDateTime) : localDateTime).toISOString()
        }
      });

      if (bookError || !bookData?.ok) {
        const reason = bookData?.error || bookError?.message || 'Unable to create session';
        throw new Error(reason);
      }

      const sessionId = bookData.sessionId;

      // Send in-app notifications
      try {
        await supabase.functions.invoke('create-session-notifications', {
          body: {
            sessionId: sessionId,
            studentId: user.id,
            mentorId: mentorId,
          },
        });
      } catch (notificationError) {
        console.error('Failed to send notifications:', notificationError);
      }

      // Send email notification to mentor about the new booking
      try {
        const { data: mentorData } = await supabase
          .from('mentors')
          .select('first_name, last_name, email, timezone')
          .eq('id', mentorId)
          .single();

        await supabase.functions.invoke('send-mentor-notification', {
          body: {
            type: 'session_booked',
            sessionId: sessionId
          }
        });

        // Send calendar invite to both mentor and student
        if (mentorData?.email && user.email) {
          await supabase.functions.invoke('send-calendar-invite', {
            body: {
              sessionId: sessionId,
              mentorName: mentorName,
              mentorEmail: mentorData.email,
              studentName: `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() || user.email,
              studentEmail: user.email,
              scheduledAt: (selectedSlot.utcDateTime ? new Date(selectedSlot.utcDateTime) : localDateTime).toISOString(),
              durationMinutes: 30,
              notes: `Discovery Call - Free 30-minute consultation with ${mentorName}`,
              mentorTimezone: mentorData?.timezone || selectedSlot.mentorTimezone,
              studentTimezone: getUserTimezone()
            }
          });
        } else {
          console.warn('Cannot send calendar invite: missing email addresses', {
            mentorEmail: mentorData?.email,
            studentEmail: user.email
          });
        }
      } catch (emailError) {
        console.error('Error sending notifications:', emailError);
        // Don't fail the booking if email fails
      }

      // Track discovery call booking as a lead
      trackLead();
      
      toast({
        title: "Discovery Call Booked!",
        description: `Your free consultation with ${mentorName} has been scheduled.`,
      });

      setHasUsedDiscoveryCall(true);
      setShowBookingFlow(false);
      
      // Navigate to student dashboard
      setTimeout(() => {
        navigate('/dashboard/student');
      }, 1000);
    } catch (error: any) {
      console.error('Error booking discovery call:', error);
      toast({
        title: "Booking Failed",
        description: error?.message || "Unable to book discovery call. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return null;
  }

  if (showBookingFlow) {
    return (
      <Card className="obsidian-card border-mentor-primary">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-energy flex items-center gap-2">
                <PhoneCall className="h-5 w-5" />
                Book Your Free Discovery Call
              </CardTitle>
              <CardDescription>30-minute consultation - No tokens required</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowBookingFlow(false)}>
              Cancel
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <StreamlinedBookingFlow
            mentorId={mentorId}
            mentorName={mentorName}
            onTimeSlotSelect={setSelectedSlot}
            selectedSlot={selectedSlot}
          />
          {selectedSlot && (
            <div className="mt-4 pt-4 border-t border-border">
              <Button 
                className="w-full energy-button" 
                size="lg"
                onClick={handleBookDiscoveryCall}
              >
                Confirm Free Discovery Call
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="obsidian-card border-mentor-primary">
      <CardHeader>
        <CardTitle className="text-energy flex items-center gap-2">
          <PhoneCall className="h-5 w-5" />
          Free Discovery Call
        </CardTitle>
        <CardDescription>
          Get to know {mentorName} with a complimentary 30-minute consultation
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <Badge variant="secondary" className="mt-1">
              FREE
            </Badge>
            <div className="flex-1">
              <h4 className="font-semibold mb-2">What is a Discovery Call?</h4>
              <p className="text-sm text-muted-foreground mb-4">
                A Discovery Call is a free 30-minute session where you can meet {mentorName}, 
                discuss your goals, and see if they&apos;re the right fit for you before committing 
                to a paid session. This is your opportunity to ask questions and get comfortable 
                with your potential mentor.
              </p>
            </div>
          </div>

          {hasUsedDiscoveryCall ? (
            <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <p className="text-sm text-muted-foreground">
                You&apos;ve already used your free discovery call with this mentor
              </p>
            </div>
          ) : (
            <Button 
              className="w-full energy-button" 
              size="lg"
              onClick={() => {
                if (!isAuthenticated) {
                  // Store mentor ID for discovery call and redirect to sign up
                  safeSetItem('pendingDiscoveryCall', mentorId);
                  toast({
                    title: "Sign In Required",
                    description: "Please sign in or create an account to book your free discovery call.",
                  });
                  navigate('/auth/student');
                  return;
                }
                setShowBookingFlow(true);
              }}
            >
              <PhoneCall className="mr-2 h-5 w-5" />
              Book Your Free Discovery Call
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
